/**
 * API Service Layer
 *
 * Provides a typed HTTP client for interacting with the backend API.
 * All methods handle error responses and return typed data.
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * API base URL - uses Vite proxy in development, can be configured for production
 */
export const API_BASE_URL = '/api/v1';

// ============================================================================
// TYPES - Pagination
// ============================================================================

/**
 * Standard pagination metadata returned by list endpoints
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Standard paginated response shape
 */
export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: PaginationMeta;
}

/**
 * Standard single item response shape
 */
export interface SingleResponse<T> {
  success: boolean;
  data: T;
}

/**
 * Standard error response shape
 */
export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

// ============================================================================
// TYPES - Domain Models
// ============================================================================

/**
 * Source - scraper data source
 *
 * Matches the Prisma Source model. The backend returns raw Prisma objects,
 * so field names must match the schema exactly.
 */
export interface Source {
  id: string;
  slug: string;
  name: string;
  baseUrl: string;
  adapterType: string;
  config: Record<string, unknown>;
  isActive: boolean;
  isHealthy: boolean;
  lastHealthCheck: string | null;
  lastScrapeAt: string | null;
  errorCount: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  _count?: {
    listings: number;
    scrapeRuns: number;
  };
  /** Included when ?includeRecentScrapeRuns=true on GET /sources/:id */
  scrapeRuns?: ScrapeRunSummary[];
}

/**
 * Compact scrape run summary returned by includeRecentScrapeRuns
 */
export interface ScrapeRunSummary {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startedAt: string | null;
  completedAt: string | null;
  listingsFound: number;
  listingsNew: number;
  listingsUpdated: number;
  errorsCount: number;
}

/**
 * Area - geographic region
 */
export interface Area {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  parentId: string | null;
  parent?: Area | null;
  children?: Area[];
  boundingBox: Record<string, unknown> | null;
  centerLat: number | null;
  centerLng: number | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: {
    listings: number;
    children: number;
  };
}

/**
 * Listing - rental property listing
 */
export interface Listing {
  id: string;
  sourceId: string;
  source?: Source;
  externalId: string;
  externalUrl: string;
  title: string;
  description: string | null;
  price: number | null;
  currency: string;
  bedrooms: number | null;
  bathrooms: number | null;
  sqft: number | null;
  propertyType: string | null;
  address: string | null;
  neighborhood: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  latitude: number | null;
  longitude: number | null;
  areaId: string | null;
  area?: Area | null;
  images: string[];
  amenities: string[];
  petPolicy: string | null;
  parkingIncluded: boolean | null;
  utilitiesIncluded: string[];
  availableDate: string | null;
  leaseTerm: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  listingCategory: string | null;
  listingGranularity: string;
  hasEligibilityCriteria: boolean;
  rawData: Record<string, unknown>;
  contentHash: string | null;
  status: 'active' | 'expired' | 'removed' | 'flagged';
  postedAt: string | null;
  lastSeenAt: string | null;
  firstScrapedAt: string;
  canonicalId: string | null;
  canonical?: Listing | null;
  duplicates?: Listing[];
  ranking?: ListingRanking | null;
  priceHistory?: PriceHistoryEntry[];
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * PriceHistoryEntry - a single price change record
 */
export interface PriceHistoryEntry {
  id: string;
  price: number;
  recordedAt: string;
}

/**
 * ListingRanking - quality score for a listing
 */
export interface ListingRanking {
  id: string;
  listingId: string;
  listing?: Listing;
  score: number;
  priceScore: number;
  locationScore: number;
  qualityScore: number;
  completenessScore: number;
  freshnessScore: number;
  weightsVersion: string;
  factors: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/**
 * NotificationPreference - user notification settings
 */
export interface NotificationPreference {
  id: string;
  userId: string;
  emailEnabled: boolean;
  emailAddress: string | null;
  emailFrequency: string;
  emailLastSentAt: string | null;
  smsEnabled: boolean;
  phoneNumber: string | null;
  smsThreshold: number;
  minPrice: number | null;
  maxPrice: number | null;
  minBedrooms: number | null;
  maxBedrooms: number | null;
  areaIds: string[];
  propertyTypes: string[];
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  _count?: {
    notificationLogs: number;
  };
}

/**
 * NotificationLog - sent notification record
 */
export interface NotificationLog {
  id: string;
  preferenceId: string;
  preference?: NotificationPreference;
  channel: string;
  status: string;
  subject: string | null;
  body: string | null;
  recipientEmail: string | null;
  recipientPhone: string | null;
  listingIds: string[];
  listingsCount: number;
  providerMessageId: string | null;
  providerResponse: Record<string, unknown> | null;
  errorMessage: string | null;
  retryCount: number;
  scheduledAt: string | null;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * ScrapeRun - scrape operation record
 *
 * Matches the Prisma ScrapeRun model. Note: errorsCount (not errorCount)
 * to match the database column name.
 */
export interface ScrapeRun {
  id: string;
  sourceId: string;
  source?: Source;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startedAt: string | null;
  completedAt: string | null;
  listingsFound: number;
  listingsNew: number;
  listingsUpdated: number;
  errorsCount: number;
  errorMessage: string | null;
  errorStack: string | null;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/**
 * SavedListing - a user's saved/favorited listing
 */
export interface SavedListing {
  id: string;
  listingId: string;
  notes: string | null;
  createdAt: string;
  listing: Listing;
}

/**
 * Health check response
 */
export interface HealthStatus {
  status: 'ok' | 'degraded' | 'unhealthy';
  timestamp: string;
  uptime?: number;
  version?: string;
  services?: {
    database?: { status: 'ok' | 'error'; message?: string };
    redis?: { status: 'ok' | 'error'; message?: string };
  };
}

/**
 * Ranking configuration
 */
export interface RankingConfig {
  weights: {
    price: number;
    location: number;
    quality: number;
    completeness: number;
    freshness: number;
  };
  config: {
    idealPriceMin: number;
    idealPriceMax: number;
    priceUpperBound: number;
    preferredNeighborhoods: string[];
    qualityImageMinCount: number;
    qualityImageGoodCount: number;
    qualityDescriptionMinLength: number;
    qualityDescriptionGoodLength: number;
    freshnessDaysNew: number;
    freshnessDaysStale: number;
  };
  version: string;
}

/**
 * Stats summary response
 */
export interface StatsSummary {
  total: number;
  [key: string]: number | Record<string, number>;
}

/**
 * Response shape for GET /sources/:id/scrape-runs
 *
 * The backend nests scrape runs under a source wrapper,
 * unlike standard PaginatedResponse.
 */
export interface ScrapeRunsResponse {
  success: boolean;
  data: {
    source: { id: string; name: string; slug: string };
    scrapeRuns: ScrapeRun[];
  };
  pagination: PaginationMeta;
}

// ============================================================================
// TYPES - Query Parameters
// ============================================================================

/**
 * Common pagination parameters
 */
export interface PaginationParams {
  page?: number;
  limit?: number;
}

/**
 * Listings query parameters
 */
export interface ListingsParams extends PaginationParams {
  status?: 'active' | 'expired' | 'removed' | 'flagged';
  sourceId?: string;
  areaId?: string;
  propertyType?: string;
  minPrice?: number;
  maxPrice?: number;
  minBedrooms?: number;
  maxBedrooms?: number;
  minBathrooms?: number;
  maxBathrooms?: number;
  search?: string;
  listingCategory?: string;
  listingGranularity?: string;
  hasEligibilityCriteria?: string;
  sortBy?: 'price' | 'bedrooms' | 'postedAt' | 'lastSeenAt' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  includeSource?: boolean;
  includeArea?: boolean;
  includeRanking?: boolean;
  postedAfter?: string;
  postedBefore?: string;
  excludeDuplicates?: boolean;
}

/**
 * Sources query parameters
 */
export interface SourcesParams extends PaginationParams {
  isActive?: boolean;
  isHealthy?: boolean;
  adapterType?: string;
  search?: string;
  includeListingCount?: boolean;
  includeScrapeRunCount?: boolean;
  sortBy?: 'name' | 'slug' | 'lastScrapeAt' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Areas query parameters
 */
export interface AreasParams extends PaginationParams {
  parentId?: string | null;
  search?: string;
  includeParent?: boolean;
  includeChildren?: boolean;
  sortBy?: 'name' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Rankings query parameters
 */
export interface RankingsParams extends PaginationParams {
  minScore?: number;
  maxScore?: number;
  weightsVersion?: string;
  sortBy?: 'score' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Top rankings query parameters
 */
export interface TopRankingsParams extends PaginationParams {
  sourceId?: string;
  areaId?: string;
  propertyType?: string;
  minPrice?: number;
  maxPrice?: number;
  minBedrooms?: number;
  maxBedrooms?: number;
  minScore?: number;
}

/**
 * Notification preferences query parameters
 */
export interface NotificationPreferencesParams extends PaginationParams {
  userId?: string;
  emailEnabled?: boolean;
  smsEnabled?: boolean;
  sortBy?: 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Notification logs query parameters
 */
export interface NotificationLogsParams extends PaginationParams {
  channel?: 'email' | 'sms';
  status?: 'pending' | 'sent' | 'failed' | 'bounced';
  preferenceId?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: 'sentAt' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// TYPES - Mutation Payloads
// ============================================================================

/**
 * Create/Update notification preference payload
 */
export interface NotificationPreferencePayload {
  userId?: string;
  emailEnabled?: boolean;
  emailAddress?: string | null;
  emailFrequency?: string;
  smsEnabled?: boolean;
  phoneNumber?: string | null;
  smsThreshold?: number;
  minPrice?: number | null;
  maxPrice?: number | null;
  minBedrooms?: number | null;
  maxBedrooms?: number | null;
  areaIds?: string[];
  propertyTypes?: string[];
}

/**
 * Create/Update source payload
 */
export interface SourcePayload {
  slug?: string;
  name?: string;
  baseUrl?: string;
  adapterType?: string;
  isActive?: boolean;
  isHealthy?: boolean;
  errorCount?: number;
  config?: Record<string, unknown>;
}

/**
 * Create/Update area payload
 */
export interface AreaPayload {
  slug?: string;
  name?: string;
  description?: string | null;
  parentId?: string | null;
  boundingBox?: Record<string, unknown> | null;
  centerLat?: number | null;
  centerLng?: number | null;
}

// ============================================================================
// API CLIENT - Core Functions
// ============================================================================

/**
 * Custom API error class with additional metadata
 */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/**
 * Build query string from parameters object
 */
function buildQueryString(params: Record<string, unknown> | object): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Make an API request with error handling
 */
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle non-JSON responses (like 204 No Content)
  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json();

  if (!response.ok) {
    const error = data as ApiError;
    throw new ApiRequestError(
      error.error?.message || `Request failed with status ${response.status}`,
      response.status,
      error.error?.code,
      error.error?.details
    );
  }

  return data as T;
}

// ============================================================================
// API CLIENT - Auth
// ============================================================================

export const authApi = {
  /**
   * Verify an email address using a token from the verification email
   */
  verifyEmail: (token: string): Promise<SingleResponse<{ message: string }>> => {
    return apiRequest('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  /**
   * Request a new verification email to be sent to the given address
   */
  resendVerification: (email: string): Promise<SingleResponse<{ message: string }>> => {
    return apiRequest('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
};

// ============================================================================
// API CLIENT - Listings
// ============================================================================

export const listingsApi = {
  /**
   * Get paginated list of listings
   */
  list: (params: ListingsParams = {}): Promise<PaginatedResponse<Listing>> => {
    return apiRequest(`/listings${buildQueryString(params)}`);
  },

  /**
   * Get a single listing by ID
   */
  get: (
    id: string,
    params: { includeSource?: boolean; includeArea?: boolean; includeRanking?: boolean } = {}
  ): Promise<SingleResponse<Listing>> => {
    return apiRequest(`/listings/${id}${buildQueryString(params)}`);
  },

  /**
   * Get duplicates of a listing
   */
  getDuplicates: (id: string): Promise<SingleResponse<Listing[]>> => {
    return apiRequest(`/listings/${id}/duplicates`);
  },

  /**
   * Get listing statistics
   */
  getStats: (): Promise<SingleResponse<StatsSummary>> => {
    return apiRequest('/listings/stats/summary');
  },
};

// ============================================================================
// API CLIENT - Sources
// ============================================================================

export const sourcesApi = {
  /**
   * Get paginated list of sources
   */
  list: (params: SourcesParams = {}): Promise<PaginatedResponse<Source>> => {
    return apiRequest(`/sources${buildQueryString(params)}`);
  },

  /**
   * Get a single source by ID
   */
  get: (id: string): Promise<SingleResponse<Source>> => {
    return apiRequest(`/sources/${id}`);
  },

  /**
   * Get a source by slug
   */
  getBySlug: (slug: string): Promise<SingleResponse<Source>> => {
    return apiRequest(`/sources/slug/${slug}`);
  },

  /**
   * Get listings for a source
   */
  getListings: (id: string, params: ListingsParams = {}): Promise<PaginatedResponse<Listing>> => {
    return apiRequest(`/sources/${id}/listings${buildQueryString(params)}`);
  },

  /**
   * Get scrape runs for a source
   *
   * Note: The backend returns { data: { source, scrapeRuns }, pagination }
   * which differs from the standard PaginatedResponse shape.
   */
  getScrapeRuns: (id: string, params: PaginationParams = {}): Promise<ScrapeRunsResponse> => {
    return apiRequest(`/sources/${id}/scrape-runs${buildQueryString(params)}`);
  },

  /**
   * Create a new source
   */
  create: (payload: SourcePayload): Promise<SingleResponse<Source>> => {
    return apiRequest('/sources', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Update a source
   */
  update: (id: string, payload: SourcePayload): Promise<SingleResponse<Source>> => {
    return apiRequest(`/sources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Delete a source (soft delete)
   */
  delete: (id: string): Promise<SingleResponse<Source>> => {
    return apiRequest(`/sources/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Update source health status
   */
  updateHealth: (
    id: string,
    payload: { isHealthy: boolean; errorCount?: number }
  ): Promise<SingleResponse<Source>> => {
    return apiRequest(`/sources/${id}/health`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Trigger a manual scrape for a source
   */
  triggerScrape: (
    id: string
  ): Promise<
    SingleResponse<{ jobId: string; sourceId: string; sourceName: string; message: string }>
  > => {
    return apiRequest(`/sources/${id}/trigger-scrape`, {
      method: 'POST',
    });
  },

  /**
   * Get source statistics
   */
  getStats: (): Promise<SingleResponse<StatsSummary>> => {
    return apiRequest('/sources/stats/summary');
  },
};

// ============================================================================
// API CLIENT - Areas
// ============================================================================

export const areasApi = {
  /**
   * Get paginated list of areas
   */
  list: (params: AreasParams = {}): Promise<PaginatedResponse<Area>> => {
    return apiRequest(`/areas${buildQueryString(params)}`);
  },

  /**
   * Get a single area by ID
   */
  get: (id: string): Promise<SingleResponse<Area>> => {
    return apiRequest(`/areas/${id}`);
  },

  /**
   * Get an area by slug
   */
  getBySlug: (slug: string): Promise<SingleResponse<Area>> => {
    return apiRequest(`/areas/slug/${slug}`);
  },

  /**
   * Get listings for an area
   */
  getListings: (id: string, params: ListingsParams = {}): Promise<PaginatedResponse<Listing>> => {
    return apiRequest(`/areas/${id}/listings${buildQueryString(params)}`);
  },

  /**
   * Create a new area
   */
  create: (payload: AreaPayload): Promise<SingleResponse<Area>> => {
    return apiRequest('/areas', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Update an area
   */
  update: (id: string, payload: AreaPayload): Promise<SingleResponse<Area>> => {
    return apiRequest(`/areas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Delete an area (soft delete)
   */
  delete: (id: string): Promise<SingleResponse<Area>> => {
    return apiRequest(`/areas/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get area statistics
   */
  getStats: (): Promise<SingleResponse<StatsSummary>> => {
    return apiRequest('/areas/stats/summary');
  },
};

// ============================================================================
// API CLIENT - Rankings
// ============================================================================

export const rankingsApi = {
  /**
   * Get paginated list of rankings
   */
  list: (params: RankingsParams = {}): Promise<PaginatedResponse<ListingRanking>> => {
    return apiRequest(`/rankings${buildQueryString(params)}`);
  },

  /**
   * Get a single ranking by ID
   */
  get: (id: string): Promise<SingleResponse<ListingRanking>> => {
    return apiRequest(`/rankings/${id}`);
  },

  /**
   * Get ranking for a listing
   */
  getByListing: (listingId: string): Promise<SingleResponse<ListingRanking>> => {
    return apiRequest(`/rankings/listing/${listingId}`);
  },

  /**
   * Get top ranked listings
   */
  getTop: (params: TopRankingsParams = {}): Promise<PaginatedResponse<ListingRanking>> => {
    return apiRequest(`/rankings/top${buildQueryString(params)}`);
  },

  /**
   * Get ranking configuration
   */
  getConfig: (): Promise<SingleResponse<RankingConfig>> => {
    return apiRequest('/rankings/config');
  },

  /**
   * Get ranking statistics
   */
  getStats: (): Promise<SingleResponse<StatsSummary>> => {
    return apiRequest('/rankings/stats/summary');
  },

  /**
   * Trigger recalculation of all rankings
   */
  recalculateAll: (): Promise<SingleResponse<{ processed: number }>> => {
    return apiRequest('/rankings/recalculate', {
      method: 'POST',
    });
  },

  /**
   * Recalculate ranking for a specific listing
   */
  recalculate: (listingId: string): Promise<SingleResponse<ListingRanking>> => {
    return apiRequest(`/rankings/${listingId}/recalculate`, {
      method: 'POST',
    });
  },
};

// ============================================================================
// API CLIENT - Notifications
// ============================================================================

export const notificationsApi = {
  preferences: {
    /**
     * Get paginated list of notification preferences
     */
    list: (
      params: NotificationPreferencesParams = {}
    ): Promise<PaginatedResponse<NotificationPreference>> => {
      return apiRequest(`/notifications/preferences${buildQueryString(params)}`);
    },

    /**
     * Get a single notification preference by ID
     */
    get: (id: string): Promise<SingleResponse<NotificationPreference>> => {
      return apiRequest(`/notifications/preferences/${id}`);
    },

    /**
     * Get notification preference by user ID (returns null data for new users)
     */
    getByUser: (userId: string): Promise<SingleResponse<NotificationPreference | null>> => {
      return apiRequest(`/notifications/preferences/user/${userId}`);
    },

    /**
     * Create a new notification preference
     */
    create: (
      payload: NotificationPreferencePayload
    ): Promise<SingleResponse<NotificationPreference>> => {
      return apiRequest('/notifications/preferences', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    /**
     * Update a notification preference
     */
    update: (
      id: string,
      payload: NotificationPreferencePayload
    ): Promise<SingleResponse<NotificationPreference>> => {
      return apiRequest(`/notifications/preferences/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    },

    /**
     * Delete a notification preference (soft delete)
     */
    delete: (id: string): Promise<SingleResponse<NotificationPreference>> => {
      return apiRequest(`/notifications/preferences/${id}`, {
        method: 'DELETE',
      });
    },
  },

  logs: {
    /**
     * Get recent notification logs
     */
    getRecent: (
      params: NotificationLogsParams = {}
    ): Promise<PaginatedResponse<NotificationLog>> => {
      return apiRequest(`/notifications/logs/recent${buildQueryString(params)}`);
    },

    /**
     * Get notification logs for a specific preference
     */
    getByPreference: (
      preferenceId: string,
      params: NotificationLogsParams = {}
    ): Promise<PaginatedResponse<NotificationLog>> => {
      return apiRequest(
        `/notifications/preferences/${preferenceId}/logs${buildQueryString(params)}`
      );
    },

    /**
     * Get notification log statistics
     */
    getStats: (
      params: { startDate?: string; endDate?: string } = {}
    ): Promise<SingleResponse<StatsSummary>> => {
      return apiRequest(`/notifications/logs/stats${buildQueryString(params)}`);
    },
  },

  /**
   * Get notification statistics
   */
  getStats: (): Promise<SingleResponse<StatsSummary>> => {
    return apiRequest('/notifications/stats/summary');
  },
};

// ============================================================================
// API CLIENT - Saved Listings
// ============================================================================

export const savedListingsApi = {
  /**
   * Get the current user's saved listings (paginated)
   */
  list: (params: PaginationParams = {}): Promise<PaginatedResponse<SavedListing>> => {
    return apiRequest(`/saved-listings${buildQueryString(params)}`);
  },

  /**
   * Save/favorite a listing
   */
  save: (payload: { listingId: string; notes?: string }): Promise<SingleResponse<SavedListing>> => {
    return apiRequest('/saved-listings', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Unsave/unfavorite a listing
   */
  unsave: (listingId: string): Promise<void> => {
    return apiRequest(`/saved-listings/${listingId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Check if a single listing is saved
   */
  check: (listingId: string): Promise<SingleResponse<{ saved: boolean }>> => {
    return apiRequest(`/saved-listings/check/${listingId}`);
  },

  /**
   * Batch check multiple listings
   */
  batchCheck: (listingIds: string[]): Promise<SingleResponse<Record<string, boolean>>> => {
    return apiRequest(`/saved-listings/check?listingIds=${listingIds.join(',')}`);
  },
};

// ============================================================================
// API CLIENT - Health
// ============================================================================

export const healthApi = {
  /**
   * Get overall health status
   */
  getStatus: (): Promise<HealthStatus> => {
    return apiRequest('/health');
  },

  /**
   * Get readiness status
   */
  getReady: (): Promise<HealthStatus> => {
    return apiRequest('/health/ready');
  },

  /**
   * Get liveness status
   */
  getLive: (): Promise<HealthStatus> => {
    return apiRequest('/health/live');
  },

  /**
   * Get database health status
   */
  getDb: (): Promise<HealthStatus> => {
    return apiRequest('/health/db');
  },

  /**
   * Get Redis health status
   */
  getRedis: (): Promise<HealthStatus> => {
    return apiRequest('/health/redis');
  },
};

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * Combined API client object for convenient imports
 */
export const api = {
  auth: authApi,
  listings: listingsApi,
  sources: sourcesApi,
  areas: areasApi,
  rankings: rankingsApi,
  notifications: notificationsApi,
  savedListings: savedListingsApi,
  health: healthApi,
} as const;

export default api;
